import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertCircle, CheckCircle, Loader2, Database } from 'lucide-react';
import { toast } from 'sonner';

const MigrationPage = () => {
  const [checking, setChecking] = useState(false);
  const [running, setRunning] = useState(false);
  const [checkResult, setCheckResult] = useState(null);
  const [migrationResult, setMigrationResult] = useState(null);

  const handleCheck = async () => {
    setChecking(true);
    setCheckResult(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/migration/check-duplicates`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to check duplicates');
      }

      const data = await response.json();
      setCheckResult(data);
      
      if (data.needs_migration) {
        toast.warning(`Found ${data.duplicates_found} duplicate bookings`);
      } else {
        toast.success('Database is clean!');
      }
    } catch (error) {
      console.error('Check error:', error);
      const errorMessage = error.message || 'Unknown error occurred';
      toast.error('Failed to check database: ' + errorMessage);
    } finally {
      setChecking(false);
    }
  };

  const handleRunMigration = async () => {
    if (!window.confirm('Are you sure you want to run the migration? This will:\n\n' +
      '1. Cancel duplicate bookings\n' +
      '2. Refund credits to affected users\n' +
      '3. Create unique index\n\n' +
      'This action cannot be undone.')) {
      return;
    }

    setRunning(true);
    setMigrationResult(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/migration/fix-duplicate-bookings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Migration failed');
      }

      const data = await response.json();
      setMigrationResult(data);
      
      if (data.status === 'success') {
        toast.success('Migration completed successfully!');
        // Refresh check results
        handleCheck();
      } else {
        toast.error('Migration completed with errors');
      }
    } catch (error) {
      console.error('Migration error:', error);
      const errorMessage = error.message || 'Unknown error occurred';
      toast.error('Migration failed: ' + errorMessage);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Database Migration</h1>
        <p className="text-gray-600 mt-2">Fix duplicate mock bookings and ensure database integrity</p>
      </div>

      {/* Check Status Card */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5" />
            Database Status Check
          </CardTitle>
          <CardDescription>
            Check for duplicate bookings and verify unique index
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button 
            onClick={handleCheck}
            disabled={checking}
            className="w-full sm:w-auto"
          >
            {checking ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Checking...
              </>
            ) : (
              'Check Database Status'
            )}
          </Button>

          {checkResult && (
            <div className="mt-4 space-y-3">
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Total Bookings</p>
                      <p className="text-2xl font-bold">{checkResult.total_bookings}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Duplicates Found</p>
                      <p className="text-2xl font-bold text-red-600">{checkResult.duplicates_found}</p>
                    </div>
                  </div>
                  
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center gap-2">
                      {checkResult.unique_index_exists ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-red-600" />
                      )}
                      <span className="text-sm">
                        Unique Index: {checkResult.unique_index_exists ? 'Exists' : 'Missing'}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {checkResult.needs_migration ? (
                        <AlertCircle className="w-5 h-5 text-orange-600" />
                      ) : (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      )}
                      <span className="text-sm font-medium">
                        {checkResult.needs_migration ? 'Migration Required' : 'Database is Healthy'}
                      </span>
                    </div>
                  </div>

                  {checkResult.duplicate_details && checkResult.duplicate_details.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm font-medium mb-2">Duplicate Details:</p>
                      <div className="space-y-1">
                        {checkResult.duplicate_details.map((dup, idx) => (
                          <div key={idx} className="text-xs bg-white p-2 rounded">
                            User: {dup.user_id} - Count: {dup.count}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Migration Action Card */}
      {checkResult && checkResult.needs_migration && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-orange-900">
              <AlertCircle className="w-5 h-5" />
              Migration Required
            </CardTitle>
            <CardDescription className="text-orange-700">
              Run migration to fix duplicate bookings and create unique index
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="bg-white p-4 rounded-lg border border-orange-200">
                <p className="font-medium mb-2">What will happen:</p>
                <ul className="space-y-1 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Cancel {checkResult.duplicates_found * 2} older duplicate bookings</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Refund mock credits to affected users</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Create unique compound index to prevent future duplicates</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Keep the latest booking for each user-mock pair</span>
                  </li>
                </ul>
              </div>

              <Button 
                onClick={handleRunMigration}
                disabled={running}
                className="w-full bg-orange-600 hover:bg-orange-700"
              >
                {running ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Running Migration...
                  </>
                ) : (
                  'Run Migration Now'
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Migration Results */}
      {migrationResult && (
        <Card className="mt-6 border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-900">
              <CheckCircle className="w-5 h-5" />
              Migration Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white p-3 rounded">
                  <p className="text-sm text-gray-600">Duplicates Found</p>
                  <p className="text-xl font-bold">{migrationResult.duplicates_found}</p>
                </div>
                <div className="bg-white p-3 rounded">
                  <p className="text-sm text-gray-600">Bookings Cancelled</p>
                  <p className="text-xl font-bold">{migrationResult.bookings_cancelled}</p>
                </div>
                <div className="bg-white p-3 rounded">
                  <p className="text-sm text-gray-600">Credits Refunded</p>
                  <p className="text-xl font-bold">{migrationResult.credits_refunded}</p>
                </div>
              </div>

              <div className="bg-white p-4 rounded">
                <div className="flex items-center gap-2 mb-2">
                  {migrationResult.index_created ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-600" />
                  )}
                  <span className="font-medium">
                    Unique Index: {migrationResult.index_created ? 'Created Successfully' : 'Failed'}
                  </span>
                </div>
                
                <p className="text-sm text-gray-600">
                  Status: <span className="font-medium capitalize">{migrationResult.status}</span>
                </p>
                
                {migrationResult.errors && migrationResult.errors.length > 0 && (
                  <div className="mt-3 p-3 bg-red-50 rounded">
                    <p className="text-sm font-medium text-red-900 mb-1">Errors:</p>
                    {migrationResult.errors.map((error, idx) => (
                      <p key={idx} className="text-xs text-red-700">{error}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MigrationPage;
